import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Paper,
  alpha,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  SecurityOutlined,
  SaveOutlined,
  AddModeratorOutlined,
} from '@mui/icons-material';
import { withRouter } from 'react-router-dom';
import { getPrivileges, createRole } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import ConfirmModal from './ConfirmModal';
import ModalHeader from './ModalHeader';
import './CreateRole.css';

const CreateRoleModal = withRouter(({ close, open }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [visible, setVisible] = useState(false);

  // Form State
  const [roleData, setRoleData] = useState({
    roleName: '',
    roleDescription: '',
    userCategory: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    if (open) {
      refreshPrivileges();
    }
  }, [open]);

  const refreshPrivileges = () => {
    setLoading(true);
    getPrivileges()
      .then((res) => {
        setPermissions(res.result.data);
      })
      .catch((error) => {
        openNotification({
          type: 'error',
          title: 'Error getting permissions',
          message: error.message,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const id = e.target.id;
    if (e.target.checked) {
      setSelectedPermissions((prev) => [...prev, id]);
    } else {
      setSelectedPermissions((prev) => prev.filter((p) => p !== id));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (selectedPermissions.length === 0) {
      const msg = 'You have to select at least one permission';
      setErrorMessage(msg);
      return openNotification({ type: 'error', title: msg });
    }

    setLoading(true);
    const payload = {
      roleName: roleData.roleName,
      privilegeId: selectedPermissions,
      userCategory: roleData.userCategory,
      roleDescription: roleData.roleDescription,
      scope: 1,
    };

    createRole(payload)
      .then(() => {
        setVisible(true);
      })
      .catch((error) => {
        openNotification({
          type: 'error',
          title: 'Error Creating Role',
          message: error.message,
        });
      })
      .finally(() => setLoading(false));
  };

  const closeModal = () => {
    setVisible(false);
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: '20px', overflow: 'hidden' },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              bgcolor: alpha('#1976d2', 0.1),
              color: 'primary.main',
              display: 'flex',
            }}
          >
            <AddModeratorOutlined fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Create New Role
          </Typography>
        </Stack>
        <IconButton onClick={close} size="small" sx={{ bgcolor: alpha('#000', 0.05) }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 4 }}>
        <ConfirmModal open={visible} close={closeModal} />

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* ROLE NAME */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required
                name="roleName"
                label="Role Name"
                placeholder="e.g. System Auditor"
                value={roleData.roleName}
                onChange={handleInputChange}
                size="small"
                slotProps={{ input: { sx: { borderRadius: '10px' } } }}
              />
            </Grid>

            {/* CATEGORY */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel>User Category</InputLabel>
                <Select
                  name="userCategory"
                  value={roleData.userCategory}
                  label="User Category"
                  onChange={handleInputChange}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value={2}>EDM</MenuItem>
                  <MenuItem value={1}>REGULATOR</MenuItem>
                  <MenuItem value={3}>CONSULTANT</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* DESCRIPTION */}
            <Grid size={12}>
              <TextField
                fullWidth
                required
                name="roleDescription"
                label="Role Description"
                placeholder="Describe the responsibilities of this role..."
                value={roleData.roleDescription}
                onChange={handleInputChange}
                multiline
                rows={2}
                size="small"
                slotProps={{ input: { sx: { borderRadius: '10px' } } }}
              />
            </Grid>

            <Grid size={12}>
              <Divider sx={{ my: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SecurityOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    Permissions Matrix
                  </Typography>
                </Stack>
              </Divider>

              {errorMessage && (
                <Typography color="error" variant="caption" sx={{ mt: 1, fontWeight: 700 }}>
                  {errorMessage}
                </Typography>
              )}

              {/* PERMISSIONS CONTAINER */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  maxHeight: '350px',
                  overflowY: 'auto',
                  bgcolor: alpha('#F4F6F8', 0.6),
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {loading ? (
                  <Stack alignItems="center" py={5} spacing={2}>
                    <CircularProgress size={30} />
                    <Typography variant="caption">Loading Permissions Structure...</Typography>
                  </Stack>
                ) : (
                  <Grid container spacing={2}>
                    {permissions.map((group) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={group.permission}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: alpha('#0c5bab', 0.1),
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              mb: 1,
                              color: 'primary.main',
                              fontSize: '0.75rem',
                            }}
                          >
                            {group.permission}
                          </Typography>
                          <Stack>
                            {group.privilege.map((priv) => (
                              <FormControlLabel
                                key={priv.privilegeId}
                                control={
                                  <Checkbox
                                    id={priv.privilegeId}
                                    size="small"
                                    checked={selectedPermissions.includes(priv.privilegeId)}
                                    onChange={handleCheckboxChange}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{ textTransform: 'capitalize', fontSize: '0.85rem' }}
                                  >
                                    {priv.privilegeName.toLowerCase()}
                                  </Typography>
                                }
                              />
                            ))}
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>

            {/* ACTION BUTTONS */}
            <Grid size={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  onClick={close}
                  disabled={loading}
                  sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />
                  }
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 4 }}
                >
                  Create Role
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

export default CreateRoleModal;
